/** One CSV column: its header, and how to read a row's value. */
export interface CsvColumn<T> {
  label: string;
  value: (row: T) => unknown;
}

function cell(value: unknown): string {
  if (value === null || value === undefined) return '';
  const text =
    value instanceof Date ? value.toISOString() : typeof value === 'object' ? JSON.stringify(value) : String(value);
  // Quote anything with a comma, quote or line break; double inner quotes (RFC 4180).
  // A leading =, +, - or @ is prefixed with ' so spreadsheets don't run it as a formula.
  const safe = /^[=+\-@]/.test(text) && !/^-?\d+(\.\d+)?$/.test(text) ? `'${text}` : text;
  return /[",\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

/** Rows as CSV text: a header line, then one line per row (CRLF, as Excel expects). */
export function toCsv<T>(rows: readonly T[], columns: readonly CsvColumn<T>[]): string {
  const lines = [columns.map((c) => cell(c.label)).join(',')];
  for (const row of rows) lines.push(columns.map((c) => cell(c.value(row))).join(','));
  return lines.join('\r\n');
}

/** "reservations-2026-10-21.csv". */
export function csvFilename(name: string, date: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${name}-${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}.csv`;
}

/** Saves CSV text as a file in the browser (with a BOM so Excel reads UTF-8). */
export function downloadCsv(filename: string, csv: string): void {
  if (typeof document === 'undefined') return;
  const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Epoch seconds → "2026-10-21" for a CSV cell ('' when missing). */
export function csvDate(seconds: unknown): string {
  const n = Number(seconds);
  if (!Number.isFinite(n) || n <= 0) return '';
  const d = new Date(n * 1000);
  const pad = (x: number) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** An ISO date or date-time string → its "2026-10-21" part ('' when missing). */
export function csvIsoDay(value: unknown): string {
  return value ? String(value).slice(0, 10) : '';
}
